import { useEffect, useState } from 'react';

import { useCms, useContent } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { Checkbox } from '@eneco/sparky';
import { useFormContext, useFormState } from 'react-hook-form';
import { string } from 'yup';

import type { FormFieldComponent, FormValues } from './types';
import { readFormField, useFormField } from './useFormField';

const ROLE = 'checkboxFormField';
const FALLBACK_NAME = 'checkboxFormField';
const FALLBACK_VALUE = 'true';

/**
 * `GenericFormCheckboxWithValueField` for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors
 * `libs/sitecore/components/forms/src/GenericForm/Components/GenericFormCheckboxWithValueField/GenericFormCheckboxWithValueField.tsx`.
 *
 * Unlike `GenericFormCheckboxField`, which submits a boolean, this one submits a *string* taken from
 * `checkboxValueText` — and only when ticked. That is why the value rides on a hidden input that is
 * mounted only while checked, and why the visible `Checkbox` is deliberately registered under the
 * literal name `"checkbox"`: react-hook-form must not bind the real field name to the box itself, or
 * the boolean would overwrite the string. Kept exactly as the legacy component has it.
 *
 * The descriptor (`name`/`label`/`hint`/`requiredMessage`) is the `genericFields` family entry with
 * `role === 'checkboxFormField'` — the only role the generated schema declares for this block
 * (`node_modules/@eneco/cms-sanity-schema/dist/sections/GenericFormCheckboxWithValueField.js`).
 */
const GenericFormCheckboxWithValueField: FormFieldComponent = () => {
  const { adapter } = useCms();
  const content = useContent<{ checkboxValueText?: string }>();
  const { register, unregister } = useFormContext();
  const { errors } = useFormState<FormValues>();
  const [checked, setChecked] = useState(false);

  const field = useFormField('genericFields', ROLE);
  const name = field.name || FALLBACK_NAME;
  const value = content?.checkboxValueText || FALLBACK_VALUE;
  const hint = adapter.toRichText(field.hint);

  useEffect(() => {
    // The value reaches the submission only while ticked.
    if (!checked) unregister(name);
  }, [unregister, checked, name]);

  return (
    <>
      {checked && <input type="hidden" value={value} {...register(name)} />}
      <Checkbox
        error={errors?.[name]?.message as string | undefined}
        hint={hint ? <RichText html={hint} /> : undefined}
        label={field.label ?? ''}
        onChange={event => setChecked(event.target.checked)}
        name="checkbox"
      />
    </>
  );
};

// The submitted value is the string on the hidden input, which only exists while ticked — so an
// unticked box leaves the field absent and `required` is what fails it, with the author's own message.
GenericFormCheckboxWithValueField.formSchema = content => {
  const field = readFormField(content, 'genericFields', ROLE);
  const name = field.name || FALLBACK_NAME;
  return { [name]: field.requiredMessage ? string().required(field.requiredMessage) : string() };
};

export default GenericFormCheckboxWithValueField;
